(() => {
  const GRID_SELECTOR = '#product-grid';
  const MODULE_SELECTOR = '[data-collection-callout]';
  const CARD_SELECTOR = ':scope > .grid__item';

  const MIN_PRODUCTS_LATER_PAGES = 8;

  let resizeTimer = null;
  let placementFrame = null;

  function getRenderedColumnCount(grid, cards) {
    if (!grid || !cards.length) return 0;

    const gridStyles = window.getComputedStyle(grid);
    const gap = parseFloat(gridStyles.columnGap) || 0;

    const cardWidth = cards[0].getBoundingClientRect().width;
    const gridWidth = grid.getBoundingClientRect().width;

    if (!cardWidth || !gridWidth) return 0;

    return Math.max(
      1,
      Math.round(
        (gridWidth + gap) /
        (cardWidth + gap)
      )
    );
  }

  function updateColumnClass(
    module,
    columns,
    forceFullWidth = false
  ) {
    Array.from(module.classList).forEach((className) => {
      if (
        className.startsWith('collection-callout--') &&
        className.endsWith('-col-grid')
      ) {
        module.classList.remove(className);
      }
    });

    module.classList.remove(
      'collection-callout--full-width'
    );

    if (forceFullWidth) {
      module.classList.add(
        'collection-callout--full-width'
      );
      return;
    }

    module.classList.add(
      `collection-callout--${columns}-col-grid`
    );
  }

  function adjustForTwoColumnCallout(
    targetCount,
    columns,
    cardsLength
  ) {
    targetCount = Math.min(
      Math.max(targetCount, 0),
      cardsLength
    );

    if (columns <= 2) {
      return targetCount;
    }

    const positionInRow =
      targetCount % columns;

    if (positionInRow === 0) {
      return targetCount;
    }

    const remainingSlots =
      columns - positionInRow;

    if (remainingSlots >= 2) {
      return targetCount;
    }

    return Math.min(
      targetCount + remainingSlots,
      cardsLength
    );
  }

  function placeCollectionCallout() {
    const grid =
      document.querySelector(GRID_SELECTOR);

    if (!grid) return;

    const module =
      grid.querySelector(MODULE_SELECTOR);

    if (!module) return;

    const cards = Array.from(
      grid.querySelectorAll(CARD_SELECTOR)
    );

    if (!cards.length) {
      module.hidden = true;
      module.classList.remove('is-positioned');
      return;
    }

    const columns =
      getRenderedColumnCount(grid, cards);

    if (!columns) return;

    /*
     * Full-width cases:
     *
     * <= 3 products:
     * short collection
     *
     * exactly 8 products:
     * two complete 4-card rows on desktop,
     * so the callout works better as a full-width
     * closer underneath the collection.
     */
    const isFullWidthCollection =
      (cards.length >= 2 && cards.length <= 3) ||
      cards.length === 8;

    updateColumnClass(
      module,
      columns,
      isFullWidthCollection
    );

    const currentPage = Math.max(
      1,
      parseInt(
        module.dataset.currentPage,
        10
      ) || 1
    );

    const rowsBefore = Math.max(
      1,
      parseInt(
        module.dataset.rowsBefore,
        10
      ) || 2
    );

    const laterPageRowsAfter = Math.max(
      1,
      parseInt(
        module.dataset.laterPageRowsAfter,
        10
      ) || 2
    );

    const desktopSide =
      module.dataset.desktopSide || 'right';

    let targetCount;

    /*
     * Full-width collections always place
     * the callout after all rendered products.
     */
    if (isFullWidthCollection) {
      targetCount = cards.length;
    }

    /*
     * Page 1:
     * place after configured number of rows.
     */
    else if (currentPage === 1) {
      targetCount =
        columns * rowsBefore;
    }

    /*
     * Page 2+:
     * hide if there are 8 or fewer rendered products.
     */
    else {
      if (
        cards.length <=
        MIN_PRODUCTS_LATER_PAGES
      ) {
        module.hidden = true;
        module.classList.remove('is-positioned');
        return;
      }

      const productsAfterModule =
        columns * laterPageRowsAfter;

      targetCount =
        cards.length -
        productsAfterModule;

      /*
       * Snap backward to a complete row.
       */
      targetCount =
        Math.floor(
          targetCount / columns
        ) * columns;

      /*
       * Never place before at least one row.
       */
      targetCount =
        Math.max(
          columns,
          targetCount
        );
    }

    /*
     * Right-side placement for normal
     * 2-column callout mode.
     *
     * 4 columns:
     * [ product ][ product ][ callout x2 ]
     *
     * 3 columns:
     * [ product ][ callout x2 ]
     */
    if (
      !isFullWidthCollection &&
      desktopSide === 'right'
    ) {
      if (columns >= 4) {
        targetCount +=
          columns - 2;
      } else if (columns === 3) {
        targetCount += 1;
      }
    }

    /*
     * Make sure a normal 2-column callout
     * never gets inserted with only one
     * grid slot remaining.
     */
    if (!isFullWidthCollection) {
      targetCount =
        adjustForTwoColumnCallout(
          targetCount,
          columns,
          cards.length
        );
    }

    const insertionCount = Math.min(
      targetCount,
      cards.length
    );

    if (insertionCount < 1) {
      module.hidden = true;
      module.classList.remove('is-positioned');
      return;
    }

    const targetCard =
      cards[insertionCount - 1];

    if (!targetCard) {
      module.hidden = true;
      module.classList.remove('is-positioned');
      return;
    }

    /*
     * Prevent repeated DOM movement and
     * MutationObserver flashing.
     */
    if (
      targetCard.nextElementSibling !==
      module
    ) {
      targetCard.after(module);
    }

    module.hidden = false;
    module.classList.add('is-positioned');
  }

  function schedulePlacement() {
    if (placementFrame) {
      cancelAnimationFrame(
        placementFrame
      );
    }

    placementFrame =
      requestAnimationFrame(() => {
        placementFrame = null;
        placeCollectionCallout();
      });
  }

  function handleResize() {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(() => {
      schedulePlacement();
    }, 100);
  }

  function initCollectionCallout() {
    schedulePlacement();

    window.addEventListener(
      'resize',
      handleResize
    );

    /*
     * Shopify filtering/sorting may replace
     * collection markup through AJAX.
     */
    const observer =
      new MutationObserver(
        (mutations) => {
          const relevantChange =
            mutations.some(
              (mutation) => {
                if (
                  mutation.type !== 'childList'
                ) {
                  return false;
                }

                const changedNodes = [
                  ...mutation.addedNodes,
                  ...mutation.removedNodes
                ];

                return changedNodes.some(
                  (node) => {
                    if (
                      !(node instanceof Element)
                    ) {
                      return false;
                    }

                    /*
                     * Ignore movement of the
                     * callout itself.
                     */
                    if (
                      node.matches?.(
                        MODULE_SELECTOR
                      )
                    ) {
                      return false;
                    }

                    return (
                      node.matches?.(
                        GRID_SELECTOR
                      ) ||
                      node.matches?.(
                        '.grid__item'
                      ) ||
                      node.querySelector?.(
                        GRID_SELECTOR
                      ) ||
                      node.querySelector?.(
                        '.grid__item'
                      )
                    );
                  }
                );
              }
            );

          if (relevantChange) {
            schedulePlacement();
          }
        }
      );

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    /*
     * Important for your theme:
     * opening/closing the filter sidebar
     * can change the rendered column count
     * without changing viewport width.
     */
    if ('ResizeObserver' in window) {
      const resizeObserver =
        new ResizeObserver(() => {
          schedulePlacement();
        });

      const gridContainer =
        document.querySelector(
          '#ProductGridContainer'
        );

      if (gridContainer) {
        resizeObserver.observe(
          gridContainer
        );
      }
    }
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initCollectionCallout
    );
  } else {
    initCollectionCallout();
  }
})();